"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Message } from "ai";
import { 
  MessageCircle,
  X, 
  User, 
  Bot, 
  LogIn, 
  UserPlus,
  Mail,
  Lock,

  Loader2,
  ChevronDown,
  Plus,
  Trash2
} from "lucide-react";
import { Button } from "../ui/button";

import { ChatWindow } from "./ChatWindow";
import { cn } from "../lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { TextGenerateEffect } from "./TextGenerateEffect";

export interface ExpandableChatWidgetProps {
  endpoint?: string;
  placeholder?: string;
  className?: string;
  organizationId?: string;
  publicScope?: boolean;
  allowAllAccess?: boolean;
  // Base URL for API calls (required for production deployment)
  baseUrl?: string;
  // Authentication state passed from parent
  isSignedIn?: boolean;
  user?: any;
  userLoaded?: boolean;
  onSignIn?: (email: string, password: string) => Promise<void>;
  onSignUp?: (email: string, password: string, fullName: string) => Promise<void>;
  authLoading?: boolean;
  authError?: string | null;
  // Branding
  logoUrl?: string;
  logoAlt?: string;
  brandName?: string;
  // Widget styling
  widgetClassName?: string;
  bubbleClassName?: string;
  inputClassName?: string;
  buttonsClassName?: string;
  dropdownClassName?: string;
  zIndex?: number;
  // Widget positioning
  direction?: 'up' | 'down';
  dropdownDirection?: 'top' | 'bottom';
  // CORS handling
  useProxy?: boolean;
  proxyUrl?: string;
  corsHeaders?: Record<string, string>;
  // Development - allow localhost testing
  allowLocalhost?: boolean;
  // Toast notification handler
  onToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

type Conversation = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  organization_id?: string;
  messages: {
    id: string;
    content: string;
    role: string;
    created_at: string;
  }[];
};

export function ExpandableChatWidget({ 
  endpoint = "/api/chat/retrieval_agents",
  placeholder = "Ask me anything...",
  className,
  organizationId,
  publicScope = false,
  allowAllAccess = false,
  // Base URL for API calls
  baseUrl = "https://zynextechai.com",
  // Auth props
  isSignedIn = false,
  user = null,
  userLoaded = true,
  onSignIn,
  onSignUp,
  authLoading = false,
  authError = null,
  // Branding props
  logoUrl = "/images/logos/logo.png",
  logoAlt = "ZynexTech AI Logo", 
  brandName = "ZynexTech AI",
  // Widget styling
  widgetClassName = 'h-[600px] w-[500px]',
  bubbleClassName,
  inputClassName = 'min-h-[90px] mx-auto w-[98%]',
  buttonsClassName = 'absolute bottom-3 right-3 text-[10px]',
  dropdownClassName = 'pt-2 space-x-2',
  zIndex = 9999,
  // Widget positioning
  direction = 'down',
  dropdownDirection = 'bottom',
  // CORS handling
  useProxy = false,
  proxyUrl,
  corsHeaders = {},
  // Development - allow localhost testing
  allowLocalhost = false,
  // Toast handler
  onToast
}: ExpandableChatWidgetProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  
  // Conversation management for auth users
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [initialMessages, setInitialMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  
  // Guest user management
  const [guestUserId, setGuestUserId] = useState<string | null>(null);
  const [guestConversationId, setGuestConversationId] = useState<string | null>(null);

  // Toast helper
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (onToast) {
      onToast(message, type);
    } else {
      // Fallback to console if no toast handler provided
      console.log(`[${type.toUpperCase()}] ${message}`);
    }
  }, [onToast]);

  // Helper function to build API URLs
  const buildApiUrl = useCallback((path: string) => {
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${baseUrl}/${cleanPath}`;
  }, [baseUrl]);

  // Create a conversation for guest user
  const createGuestConversation = async (guestUserId: string, organizationId: string): Promise<string | null> => {
    try {
      const requestBody = {
        title: 'Guest Chat',
        user_id: guestUserId,
        organization_id: organizationId,
        publicScope: true
      };

      // Direct request - no proxy needed since middleware handles CORS
      const response = await fetch(buildApiUrl('/api/conversations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Created guest conversation:', data.conversation?.id || data.id);
        return data.conversation?.id || data.id;
      } else {
        console.error('Failed to create guest conversation:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error creating guest conversation:', error);
      return null;
    }
  };
  
  // Auth popup handling
  const openAuthPopup = useCallback((type: 'login' | 'signup') => {
    const authUrl = `${baseUrl}/${type}`;
    const popup = window.open(
      authUrl,
      'auth',
      'width=400,height=600,scrollbars=yes,resizable=yes'
    );
    
    // Listen for popup close or auth success
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        // Refresh widget state after auth - the parent page will handle auth state
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }, 1000);
    
    // Also listen for messages from popup (in case auth succeeds)
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== baseUrl) return;
      if (event.data?.type === 'auth_success') {
        popup?.close();
        window.removeEventListener('message', handleMessage);
        clearInterval(checkClosed);
        // Refresh widget state
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Cleanup after 5 minutes
    setTimeout(() => {
      clearInterval(checkClosed);
      window.removeEventListener('message', handleMessage);
      if (popup && !popup.closed) {
        popup.close();
      }
    }, 300000);
  }, [baseUrl]);

  

  // Generate guest user ID and conversation ID for guest users
  useEffect(() => {
    const setupGuestUser = async () => {
      if (!isSignedIn && publicScope && userLoaded && organizationId) {
        // Create a browser-specific identifier to differentiate users on same device/org
        // Make it more robust for mobile browsers
        let browserFingerprint: string;
        let existingGuestUserId: string;
        let existingConversationId: string;
        
        try {
          // Try to use localStorage first
          browserFingerprint = localStorage.getItem('browser-fingerprint') || '';
          if (!browserFingerprint) {
            browserFingerprint = `${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
            localStorage.setItem('browser-fingerprint', browserFingerprint);
          }
          
          const guestUserKey = `guest-user-${organizationId}-${browserFingerprint}`;
          const guestConvKey = `guest-conversation-${organizationId}-${browserFingerprint}`;
          
          existingGuestUserId = localStorage.getItem(guestUserKey) || '';
          existingConversationId = localStorage.getItem(guestConvKey) || '';
          
          if (!existingGuestUserId) {
            existingGuestUserId = `guest_${organizationId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem(guestUserKey, existingGuestUserId);
          }
          
          // Validate existing conversation ID - clear if invalid format
          if (existingConversationId) {
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (!uuidPattern.test(existingConversationId)) {
              console.log('Clearing invalid conversation ID:', existingConversationId);
              localStorage.removeItem(guestConvKey);
              existingConversationId = '';
            }
          }
          
          if (!existingConversationId) {
            // Create a proper UUID conversation for guest user upfront
            const newConversationId = await createGuestConversation(existingGuestUserId, organizationId);
            if (newConversationId) {
              existingConversationId = newConversationId;
              localStorage.setItem(guestConvKey, existingConversationId);
            } else {
              // Don't set a conversation ID if creation fails - let the chat handle it
              console.warn('Failed to create guest conversation, will create on first message');
              return;
            }
          }
        } catch (error) {
          // Fallback for mobile browsers that might block localStorage
          console.warn('localStorage not available, using session-based guest IDs:', error);
          browserFingerprint = `mobile_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
          existingGuestUserId = `guest_${organizationId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          existingConversationId = ''; // Initialize as empty string
        }
        
        console.log('Guest user setup:', { existingGuestUserId, existingConversationId, browserFingerprint });
        setGuestUserId(existingGuestUserId);
        setGuestConversationId(existingConversationId);
      }
    };

    setupGuestUser();
  }, [isSignedIn, publicScope, userLoaded, organizationId]);

  // Fetch conversations for authenticated users
  const fetchConversations = useCallback(async () => {
    if (!isSignedIn) return;
    
    try {
      setConversationsLoading(true);
      
      // Direct request - no proxy needed since middleware handles CORS
      const response = await fetch(buildApiUrl("/api/conversations"));
      
      if (response.ok) {
        const data = await response.json();
        const sortedConversations = data.conversations.sort(
          (a: Conversation, b: Conversation) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
        setConversations(sortedConversations);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setConversationsLoading(false);
    }
  }, [isSignedIn]);

  // Fetch messages for a specific conversation (auth users only)
  const fetchMessages = useCallback(async (conversationId: string) => {
    if (!isSignedIn) return;
    
    try {
      setMessagesLoading(true);
      
      const apiUrl = buildApiUrl(`/api/conversations/${conversationId}/messages`);
      // Direct request - no proxy needed since middleware handles CORS
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          const formattedMessages = data.messages.map((msg: any) => ({
            id: msg.id,
            content: msg.content,
            role: msg.role as 'user' | 'assistant' | 'system'
          }));
          
          setInitialMessages(formattedMessages);
        } else {
          setInitialMessages([]);
        }
      } else {
        setInitialMessages([]);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      setInitialMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [isSignedIn]);

  // Fetch messages for guest conversation
  const fetchGuestMessages = useCallback(async () => {
    if (!guestUserId || !guestConversationId || !organizationId) {
      console.log('fetchGuestMessages: Missing required params', { guestUserId, guestConversationId, organizationId });
      setInitialMessages([]);
      return;
    }
    
    // Validate conversation ID format - must be a UUID, not a string pattern
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidPattern.test(guestConversationId)) {
      console.log('fetchGuestMessages: Invalid conversation ID format, skipping fetch:', guestConversationId);
      setInitialMessages([]);
      return;
    }
    
    console.log('fetchGuestMessages: Fetching messages for guest conversation', { guestUserId, guestConversationId, organizationId });
    
    try {
      setMessagesLoading(true);
      const params = new URLSearchParams({
        userId: guestUserId,
        organizationId: organizationId,
        publicScope: 'true'
      });
      
      const apiUrl = buildApiUrl(`/api/conversations/${guestConversationId}/messages?${params.toString()}`);
      console.log('fetchGuestMessages: Fetching from URL:', apiUrl);
      
      // Direct request - no proxy needed since middleware handles CORS
      const response = await fetch(apiUrl);
      
      console.log('fetchGuestMessages: Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('fetchGuestMessages: Response data:', data);
        
        if (data.messages && data.messages.length > 0) {
          const formattedMessages = data.messages.map((msg: any) => ({
            id: msg.id,
            content: msg.content,
            role: msg.role as 'user' | 'assistant' | 'system'
          }));
          
          console.log('fetchGuestMessages: Setting initial messages:', formattedMessages);
          setInitialMessages(formattedMessages);
        } else {
          console.log('fetchGuestMessages: No messages found, setting empty array');
          setInitialMessages([]);
        }
      } else if (response.status === 404) {
        // Conversation doesn't exist yet - this is normal for new guest users
        console.log('fetchGuestMessages: Guest conversation not found yet (404) - will be created on first message');
        setInitialMessages([]);
      } else {
        console.warn('fetchGuestMessages: Failed to fetch guest messages:', response.status);
        setInitialMessages([]);
      }
    } catch (error) {
      console.error("fetchGuestMessages: Error fetching guest messages:", error);
      setInitialMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [guestUserId, guestConversationId, organizationId]);

  // Fetch conversations when user signs in OR when guest user is set up
  useEffect(() => {
    if (isSignedIn && userLoaded) {
      fetchConversations();
    } else if (!isSignedIn && publicScope && guestUserId && guestConversationId && organizationId) {
      // Fetch guest conversation messages
      fetchGuestMessages();
    }
  }, [isSignedIn, userLoaded, fetchConversations, fetchGuestMessages, guestUserId, guestConversationId, publicScope, organizationId]);

  // Fetch messages when conversation changes (auth users only)
  useEffect(() => {
    if (isSignedIn && currentConversationId) {
      fetchMessages(currentConversationId);
    } else {
      setInitialMessages([]);
    }
  }, [isSignedIn, currentConversationId, fetchMessages]);

  // Handle conversation creation for both auth and guest users
  const handleConversationCreated = useCallback(async (conversationId: string) => {
    if (isSignedIn) {
      setCurrentConversationId(conversationId);
      setTimeout(() => {
        fetchConversations();
      }, 500);
    } else if (!isSignedIn && publicScope && organizationId) {
      // For guest users, update the stored conversation ID with the actual UUID from database
      console.log('Guest conversation created with actual ID:', conversationId);
      setGuestConversationId(conversationId);
      
      // Update localStorage with the actual conversation ID
      try {
        const browserFingerprint = localStorage.getItem('browser-fingerprint');
        if (browserFingerprint) {
          const guestConvKey = `guest-conversation-${organizationId}-${browserFingerprint}`;
          localStorage.setItem(guestConvKey, conversationId);
          console.log('Updated localStorage with actual conversation ID:', conversationId);
        }
      } catch (error) {
        console.warn('Could not update localStorage with conversation ID:', error);
      }
    }
  }, [isSignedIn, fetchConversations, publicScope, organizationId]);

  // Create new conversation (auth users only)
  const handleNewConversation = () => {
    if (isSignedIn) {
      setCurrentConversationId(null);
      setInitialMessages([]);
    }
  };

  // Delete conversation (auth users only)
  const handleDeleteConversation = async (conversationId: string) => {
    if (!isSignedIn) return;
    
    try {
      const response = await fetch(buildApiUrl(`/api/conversations/${conversationId}`), {
        method: "DELETE",
      });

      if (response.ok) {
        setConversations(conversations.filter((conv) => conv.id !== conversationId));
        
        if (currentConversationId === conversationId) {
          handleNewConversation();
        }
        
        showToast("Conversation deleted", "success");
      } else {
        showToast("Failed to delete conversation", "error");
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
      showToast("Failed to delete conversation", "error");
    }
  };

  // Clear guest conversation (start fresh)
  const clearGuestConversation = async () => {
    if (!isSignedIn && publicScope && organizationId && guestConversationId && guestUserId) {
      try {
        // Delete the conversation from the database first
        const params = new URLSearchParams({
          userId: guestUserId,
          organizationId: organizationId,
          publicScope: 'true'
        });
        
        const response = await fetch(buildApiUrl(`/api/conversations/${guestConversationId}?${params.toString()}`), {
          method: "DELETE",
        });

        if (!response.ok) {
          console.warn("Failed to delete guest conversation from database:", response.status);
          // Continue anyway to clear localStorage
        }

        // Create a new proper conversation for the guest user
        const newConversationId = await createGuestConversation(guestUserId, organizationId);
        if (newConversationId) {
          setGuestConversationId(newConversationId);
          
          // Update localStorage with the new conversation ID
          try {
            const browserFingerprint = localStorage.getItem('browser-fingerprint');
            if (browserFingerprint) {
              const guestConvKey = `guest-conversation-${organizationId}-${browserFingerprint}`;
              localStorage.setItem(guestConvKey, newConversationId);
            }
          } catch (error) {
            console.warn('localStorage not available for conversation clearing:', error);
          }
        } else {
          console.error('Failed to create new guest conversation');
        }
        
        showToast("Started new conversation", "success");
      } catch (error) {
        console.error("Error clearing guest conversation:", error);
        showToast("Failed to start new conversation", "error");
      }
    }
  };

  // Determine the endpoint to use
  const chatEndpoint = useMemo(() => {
    const baseEndpoint = allowAllAccess ? "/api/chat/retrieval_agents" : endpoint;
    const fullEndpoint = buildApiUrl(baseEndpoint);
    
    // Add guest parameters as query params when not signed in and publicScope is true
    if (!isSignedIn && publicScope && organizationId && guestUserId) {
      const params = new URLSearchParams({
        userId: guestUserId,
        organizationId: organizationId,
        publicScope: 'true',
        isPublicAccess: 'true'
      });
      
      // Also add conversation ID if available
      if (guestConversationId) {
        params.set('conversationId', guestConversationId);
      }
      
      const finalEndpoint = `${fullEndpoint}?${params.toString()}`;
      console.log('Guest chat endpoint:', finalEndpoint);
      console.log('Guest parameters:', { guestUserId, organizationId, guestConversationId, publicScope });
      return finalEndpoint;
    }
    
    return fullEndpoint;
  }, [allowAllAccess, endpoint, isSignedIn, publicScope, organizationId, guestUserId, buildApiUrl]);

  // Determine conversation ID to pass to ChatWindow
  const effectiveConversationId = useMemo(() => {
    if (isSignedIn) {
      return currentConversationId || undefined;
    } else {
      return guestConversationId || undefined;
    }
  }, [isSignedIn, currentConversationId, guestConversationId]);

  const handleToggleExpand = () => {
    if (!isSignedIn && userLoaded && !publicScope) {
      setShowAuth(true);
      setIsExpanded(true);
    } else {
      setIsExpanded(!isExpanded);
      setShowAuth(false);
    }
  };

  const handleMinimize = () => {
    setIsExpanded(false);
    setShowAuth(false);
  };

  const handleContinueAsGuest = () => {
    setShowAuth(false);
  };

  // Chat bubble trigger - always visible
  const ChatBubble = () => (
    <motion.button
      onClick={handleToggleExpand}
      className={cn(
        "fixed bottom-6 right-6 p-4",
        "bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg",
        "hover:shadow-xl transition-all duration-200",
        "flex items-center gap-2",
        className, // Apply custom className to bubble
        bubbleClassName // Apply bubble-specific className
      )}
      style={{ zIndex: zIndex + 1 }} // Higher z-index to stay above chat
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      animate={{ 
        opacity: isExpanded ? 0 : 1,
        scale: isExpanded ? 0.8 : 1,
        pointerEvents: isExpanded ? "none" : "auto"
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <img className="w-6 h-6 dark:invert" src={logoUrl} alt="Zynex Logo" />
      <motion.span
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: "auto", opacity: 1 }}
        className="text-sm font-medium whitespace-nowrap"
      >
        {brandName}
      </motion.span>
    </motion.button>
  );

  // Auth options - simplified to just popup triggers
  const AuthForm = () => (
    <div className="p-4 space-y-4">
      <div className="text-center space-y-2">
        <div className="w-10 h-10 bg-muted rounded-xl mx-auto flex items-center justify-center">
          <Bot className="w-5 h-5" />
        </div>
        <h3 className="text-lg font-medium">Welcome</h3>
        <p className="text-sm text-muted-foreground">
          Sign in to access personalized features
        </p>
      </div>

      {publicScope && organizationId && (
        <div className="bg-muted/50 border rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm">
            <Bot className="w-4 h-4" />
            <span className="font-medium">Guest Mode Available</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Chat with public content without signing in
          </p>
          <Button
            onClick={handleContinueAsGuest}
            variant="outline"
            size="sm"
            className="mt-2 w-full"
          >
            Continue as Guest
          </Button>
        </div>
      )}

      <div className="space-y-3">
        <Button 
          onClick={() => openAuthPopup('login')}
          className="w-full"
        >
          <LogIn className="w-4 h-4 mr-2" />
          Sign In
        </Button>
        <Button 
          onClick={() => openAuthPopup('signup')}
          variant="outline" 
          className="w-full"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Sign Up
        </Button>
      </div>
    </div>
  );

  // Empty state for ChatWindow
  const EmptyStateComponent = useMemo(() => (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-12 h-12 bg-muted rounded-xl mx-auto flex items-center justify-center mb-4">
        <img 
          className="dark:invert" 
          src={logoUrl} 
          alt={logoAlt} 
          width={20} 
          height={20} 
        />
      
      </div>
      <TextGenerateEffect words="Start a conversation" className="text-lg" />

      <p className="text-xs text-muted-foreground max-w-xs">
        {isSignedIn 
          ? "Ask me to visualize data, or use tools and knowledge sources to answer your questions."
          : publicScope 
            ? "Chat with public information and resources"
            : "Sign in to access personalized features"
        }
      </p>
    </div>
  ), [isSignedIn, publicScope]);

  // Conversation dropdown for auth users
  const ConversationDropdown = () => {
    if (!isSignedIn) return null;

    const currentConversation = conversations.find(conv => conv.id === currentConversationId);
    
    return (
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 px-2 justify-start min-w-0 flex-1 hover:bg-accent/20">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                <span className="truncate text-xs">
                  {currentConversation ? currentConversation.title : "New Chat"}
                </span>
                <ChevronDown className="w-4 h-4 flex-shrink-0" />
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuItem onClick={handleNewConversation}>
              <Plus className="w-4 h-4 mr-2" />
              New Conversation
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {conversationsLoading ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="px-2 py-1 text-sm text-muted-foreground">
                No conversations yet
              </div>
            ) : (
              conversations.slice(0, 10).map((conversation) => (
                <DropdownMenuItem
                  key={conversation.id}
                  onClick={() => setCurrentConversationId(conversation.id)}
                  className="flex items-center justify-between group"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <MessageCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{conversation.title}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConversation(conversation.id);
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  // Guest conversation controls
  const GuestControls = () => {
    if (isSignedIn || !publicScope) return null;

    return (
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0 flex-1">
                        <MessageCircle className="w-4 h-4 flex-shrink-0" />
          <span className="truncate text-xs">Guest Chat</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearGuestConversation}
          className="h-7 w-7 p-0"
          title="Start new conversation"
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  // Chat interface - memoized to prevent unnecessary re-renders
  const ChatInterface = useMemo(() => {
    console.log('ChatInterface: Rendering with', { 
      messagesLoading, 
      effectiveConversationId, 
      initialMessagesLength: initialMessages.length,
      initialMessages: initialMessages.slice(0, 2) // Log first 2 messages for debugging
    });

    if (messagesLoading) {
      return (
        <div className="flex flex-col h-full items-center justify-center">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading conversation...
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Auth Banner - minimal guest mode banner */}
        {!isSignedIn && publicScope && (
          <div className="border-b border-border/50 bg-muted/20 px-3 py-2 flex items-center justify-between text-xs flex-shrink-0">
            <span className="text-muted-foreground">💡 Get more features</span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openAuthPopup('login')}
                className="h-6 px-2 text-xs hover:bg-accent"
              >
                Sign In
              </Button>
              <Button
                size="sm"
                onClick={() => openAuthPopup('signup')}
                className="h-6 px-2 text-xs bg-foreground text-background hover:bg-foreground/90"
              >
                Sign Up
              </Button>
            </div>
          </div>
        )}
        
        <div className="flex-1 min-h-0">
          <ChatWindow
            key={`chat-${effectiveConversationId || 'default'}`}
            endpoint={chatEndpoint}
            emptyStateComponent={EmptyStateComponent}
            placeholder={placeholder}
            emoji="🤖"
            showIngestForm={false}
            showIntermediateStepsToggle={true}
            onConversationCreated={handleConversationCreated}
            conversationId={effectiveConversationId}
            initialMessages={initialMessages.length > 0 ? initialMessages : undefined}
            organizationId={organizationId}
            publicScope={publicScope}
            isAuthenticated={isSignedIn}
            baseUrl={baseUrl}
            inputClassName={inputClassName}
            buttonsClassName={buttonsClassName}
            dropdownClassName={dropdownClassName}
            dropdownDirection={dropdownDirection}
          />
        </div>
      </div>
    );
  }, [
    messagesLoading,
    chatEndpoint,
    placeholder,
    handleConversationCreated,
    effectiveConversationId,
    initialMessages,
    EmptyStateComponent,
    isSignedIn,
    publicScope,
    organizationId
  ]);

  // Expanded chat interface
  const ExpandedChat = () => (
    <motion.div
      initial={{ 
        opacity: 0, 
        scale: 0.8,
        y: 20,
      }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        y: 0,
      }}
      exit={{ 
        opacity: 0, 
        scale: 0.8,
        y: 20,
      }}
      transition={{ 
        type: "spring",
        damping: 25,
        stiffness: 300,
        mass: 0.8,
        opacity: { duration: 0.15 },
      }}
      className={cn(
        "fixed",
        "bg-background/95 backdrop-blur-sm border border-border rounded-2xl shadow-2xl",
        "flex flex-col overflow-hidden",
        // Apply widgetClassName which includes size defaults
        widgetClassName
      )}
      style={{
        // Position closer to the pill for natural expansion
        bottom: direction === 'up' ? '24px' : '24px', // Space for pill
        right: '24px',
        // Transform origin from the pill position
        transformOrigin: direction === 'up' ? 'bottom right' : 'bottom right',
        zIndex: zIndex,
      }}
    >
      {/* Header - only show when needed for conversations/auth */}
      {(isSignedIn || (!isSignedIn && publicScope)) && (
        <div className="flex items-center justify-between p-3 border-b border-border/50 bg-muted/30 flex-shrink-0">
          {isSignedIn ? (
            <ConversationDropdown />
          ) : (
            <GuestControls />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMinimize}
            className="h-7 w-7 p-0"
            title="Close chat"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Chat content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {showAuth ? (
          <AuthForm />
        ) : (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {ChatInterface}
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <>
      <ChatBubble />
      <AnimatePresence>
        {isExpanded && <ExpandedChat />}
      </AnimatePresence>
    </>
  );
} 